import { useMutation, type UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { getBffQueryData, useInfiniteQueryLPC, useQueryLPC } from 'app/lib/query';
import type { FunctionalUseInfiniteQueryOptions, FunctionalUseQueryOptions } from 'app/types/network';
import { http } from '../lib/axios';
import { useUser } from './user';
import { useAtomValue } from 'jotai/index';
import { bffUserProfileLoadedAtom, isLoggedInAtom } from 'app/atoms';
import { notificationKeys } from './keys';
import type { NotificationsType, NotificationTemplateIdentifier } from 'app/types/notification';
import { useQueryKeys } from './invalidation';

export function useAllNotifications(
  opts: FunctionalUseInfiniteQueryOptions<NotificationsType> & {
    markAsRead?: boolean;
  } = {},
  fetchOnMount = true,
) {
  const { data: user } = useUser();
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const client = useQueryClient();

  const queryKey = notificationKeys.detail(user?.id ?? '-', Boolean(opts.markAsRead));
  return useInfiniteQueryLPC<NotificationsType>({
    queryKey,
    queryFn: async ({ pageParam }) => {
      if (!user?.id) {
        return { nextPage: 0, data: [], totalCount: 0 };
      }
      const resData = (
        await http.get<NotificationsType>(`/notifications`, {
          params: { page: pageParam ?? 0, markAsRead: opts.markAsRead ? 'true' : 'false' },
        })
      ).data;

      if (opts.markAsRead && !pageParam) {
        client.setQueryData(notificationKeys.unread(), { unread_count: 0 });
      }

      return resData;
    },
    ...opts,
    enabled: (opts.enabled ?? true) && isLoggedIn && Boolean(user),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    fetchOnMount,
  });
}

export function useUnreadNotificationsCount(opts: FunctionalUseQueryOptions<{ unread_count: number }> = {}) {
  const client = useQueryClient();
  const { data: user } = useUser();
  const { refetch } = useAllNotifications({ markAsRead: false, enabled: false }, false);
  const bffUserProfileLoaded = useAtomValue(bffUserProfileLoadedAtom);

  const queryKey = notificationKeys.unread();
  return useQueryLPC<{ unread_count: number }>({
    queryKey,
    queryFn: async () => {
      if (!user?.id) {
        return { unread_count: 0 };
      }

      if (bffUserProfileLoaded) {
        const bffData = getBffQueryData<{ unread_count: number }>(client, queryKey);
        if (bffData) {
          return bffData;
        }
      }

      // For some reason, the BFF API didn't return the number of unread notifications
      // Let's load them from the previously-used API endpoint
      console.log('Loading notifications from API to count unseen...');

      const paginatedNotifications = (await refetch()).data;

      if (!paginatedNotifications?.pages[0] || paginatedNotifications.pages[0].totalCount === 0) {
        return { unread_count: 0 };
      }

      const notifications = paginatedNotifications.pages.flatMap((page) => page.data);
      const unseenNotifications = notifications.filter((notif) => !notif.seen);
      return { unread_count: unseenNotifications.length };
    },
    ...opts,
    enabled: (opts.enabled ?? true) && bffUserProfileLoaded,
  });
}

export interface CreateNotificationDTO {
  subscriberId: string;
  type: NotificationTemplateIdentifier;
  title?: string; // ns: notifications
  titleArgs?: Record<string, string>;
  message?: string; // ns: notifications
  messageArgs?: Record<string, string>;
  link?: string; // Syntax: /main/tab-name/screen-name?param1=value1[&...]
  pushLink?: string; // Syntax: navigator's linking URL, eg. /main/management/:id
  picture?: string;

  options?: Record<string, any>;
}

export function useAddOneNotification(opts: UseMutationOptions<NotificationsType, Error, CreateNotificationDTO> = {}) {
  const qk = useQueryKeys();
  return useMutation<NotificationsType, Error, CreateNotificationDTO>({
    mutationFn: (payload) => http.post<NotificationsType>('/notifications', payload).then((res) => res.data),
    ...opts,
    async onSuccess(result, variables, context) {
      await qk.allNotifications.invalidate();
      return opts.onSuccess?.(result, variables, context);
    },
  });
}
